import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Modal,
  Button,
  Group,
  Stack,
  Text,
  Loader,
  Table,
  ScrollArea,
} from "@mantine/core";
import {
  IconMail,
  IconUsers,
  IconFileInvoice,
  IconAlertTriangle,
} from "@tabler/icons-react";
import {
  SEND_NOW_PREVIEW_DEFAULT_PAGE_SIZE,
  SEND_NOW_PREVIEW_PAGE_SIZE_OPTIONS,
  useEmailTemplateSendNowCustomerDraftQuery,
  useEmailTemplateSendNowPreviewQuery,
  type EmailTemplate,
  type SendNowPreviewCustomer,
  type SendNowPreviewEmailBatch,
} from "../api/queries/emailTemplateQueries";
import { ReceivablesPagination } from "./receivables/ReceivablesPagination";
import { displayCurrencyAmount, displayISODate } from "../utils/display";

function containsHebrew(text: string): boolean {
  if (!text) return false;
  return /[\u0590-\u05FF\uFB1D-\uFDFD\uFE70-\uFEFC]/.test(text);
}

function wrapPreviewHtml(html: string, isRtl: boolean): string {
  if (!html || !isRtl) return html;
  if (/<[^>]+dir\s*=\s*["']rtl/i.test(html)) return html;
  return `<div dir="rtl" style="direction: rtl; text-align: right;">${html}</div>`;
}

function CustomerStatusPill({ status }: { status: string }) {
  if (status === "will_send") {
    return (
      <span className="inline-flex shrink-0 items-center rounded-md bg-teal-100 px-2 py-1 text-xs font-medium text-teal-900 ring-1 ring-inset ring-teal-300">
        Will send
      </span>
    );
  }

  return (
    <span className="inline-flex shrink-0 items-center rounded-md bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700 ring-1 ring-inset ring-slate-300">
      Skipped
    </span>
  );
}

function SummaryStat({
  label,
  value,
  tone = "default",
}: {
  label: string;
  value: number;
  tone?: "default" | "success" | "muted";
}) {
  const valueClass =
    tone === "success"
      ? "text-teal-700"
      : tone === "muted"
        ? "text-slate-500"
        : "text-slate-900";

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p className={`mt-1 text-2xl font-semibold ${valueClass}`}>{value}</p>
    </div>
  );
}

function EmailDraftPreview({
  batch,
  customer,
}: {
  batch: SendNowPreviewEmailBatch;
  customer: Pick<
    SendNowPreviewCustomer,
    "customer_name" | "company_name" | "recipient_emails"
  >;
}) {
  const isRtl = useMemo(
    () => containsHebrew(batch.subject) || containsHebrew(batch.body_html),
    [batch.subject, batch.body_html],
  );
  const previewBody = useMemo(
    () => wrapPreviewHtml(batch.body_html, isRtl),
    [batch.body_html, isRtl],
  );

  return (
    <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4">
      <div className="space-y-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            To
          </p>
          <p className="text-sm text-slate-800">
            {customer.recipient_emails.join(", ") || "—"}
          </p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Customer
          </p>
          <p className="text-sm text-slate-800">
            {customer.customer_name}
            {customer.company_name ? ` · ${customer.company_name}` : ""}
          </p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Subject
          </p>
          <p
            className="text-sm font-medium text-slate-900"
            dir={isRtl ? "rtl" : "ltr"}
          >
            {batch.subject}
          </p>
        </div>
        <hr className="border-dashed border-slate-300" />
        <div
          dir={isRtl ? "rtl" : "ltr"}
          className="max-h-72 overflow-y-auto whitespace-pre-wrap text-sm leading-relaxed text-slate-700"
          dangerouslySetInnerHTML={{ __html: previewBody }}
        />
        <hr className="border-dashed border-slate-300" />
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            Attachments
          </p>
          <div className="flex flex-wrap gap-2">
            {batch.invoice_external_ids.map((invoiceId) => (
              <span
                key={invoiceId}
                className="inline-flex items-center rounded border border-slate-200 bg-white px-2 py-1 text-xs font-medium text-slate-700"
              >
                {invoiceId}.pdf
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

interface RecurringTemplateSendNowPreviewModalProps {
  opened: boolean;
  template: EmailTemplate | null;
  isSending: boolean;
  onClose: () => void;
  onConfirmSend: () => void;
}

export function RecurringTemplateSendNowPreviewModal({
  opened,
  template,
  isSending,
  onClose,
  onConfirmSend,
}: RecurringTemplateSendNowPreviewModalProps) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(SEND_NOW_PREVIEW_DEFAULT_PAGE_SIZE);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    null,
  );
  const [selectedBatchIndex, setSelectedBatchIndex] = useState(0);

  const skip = (page - 1) * pageSize;
  const templateId = template?.id ?? 0;

  const previewQuery = useEmailTemplateSendNowPreviewQuery(templateId, {
    skip,
    limit: pageSize,
    queryOptions: {
      enabled: opened && !!template,
    },
  });

  const preview = previewQuery.data;
  const customers = preview?.customers ?? [];
  const totalCustomers = preview?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalCustomers / pageSize));

  const selectedCustomer = useMemo(
    () =>
      customers.find(
        (customer) => customer.customer_organization_id === selectedCustomerId,
      ) ?? null,
    [customers, selectedCustomerId],
  );

  const shouldLoadDraft =
    opened &&
    !!template &&
    selectedCustomer?.status === "will_send" &&
    (selectedCustomer.email_batch_count ?? 0) > 0;

  const customerDraftQuery = useEmailTemplateSendNowCustomerDraftQuery(
    templateId,
    shouldLoadDraft ? selectedCustomerId : null,
  );

  const draftBatches = customerDraftQuery.data?.email_batches ?? [];

  const draftBatch = useMemo(() => {
    if (!selectedCustomer || !draftBatches.length) {
      return null;
    }
    const batch = draftBatches[selectedBatchIndex] ?? draftBatches[0];
    return {
      batch,
      customer: selectedCustomer,
    };
  }, [draftBatches, selectedBatchIndex, selectedCustomer]);

  useEffect(() => {
    if (!opened) {
      setPage(1);
      setPageSize(SEND_NOW_PREVIEW_DEFAULT_PAGE_SIZE);
      setSelectedCustomerId(null);
      setSelectedBatchIndex(0);
    }
  }, [opened]);

  useEffect(() => {
    if (!opened || previewQuery.isLoading || customers.length === 0) {
      return;
    }
    const selectedOnPage = customers.some(
      (customer) => customer.customer_organization_id === selectedCustomerId,
    );
    if (selectedOnPage) {
      return;
    }
    const firstSendCustomer = customers.find(
      (customer) => customer.status === "will_send",
    );
    setSelectedCustomerId(
      firstSendCustomer?.customer_organization_id ??
        customers[0]?.customer_organization_id ??
        null,
    );
    setSelectedBatchIndex(0);
  }, [
    opened,
    customers,
    previewQuery.isLoading,
    selectedCustomerId,
    page,
    pageSize,
  ]);

  useEffect(() => {
    setSelectedBatchIndex(0);
  }, [selectedCustomerId]);

  const canSend =
    !!preview?.template_enabled && (preview?.summary.emails_to_send ?? 0) > 0;

  const isInitialLoading = previewQuery.isLoading && !preview;
  const isError = previewQuery.isError;
  const isPageFetching = previewQuery.isFetching && !!preview;

  const paginationSummary = totalCustomers
    ? `Showing ${skip + 1}–${Math.min(skip + pageSize, totalCustomers)} of ${totalCustomers} customers`
    : "No customers matched";

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size="90%"
      title={
        <Group gap="xs">
          <IconMail size={20} />
          <Text size="lg" fw={600}>
            Send now preview
          </Text>
          {template ? (
            <Text size="sm" c="dimmed">
              {template.name}
            </Text>
          ) : null}
        </Group>
      }
      radius="md"
      padding="xl"
      styles={{
        content: {
          maxWidth: "1200px",
        },
        body: {
          paddingTop: "0.5rem",
        },
      }}
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Review matched customers and invoices before sending. Send now bypasses
          the repeat interval but still respects opt-outs and the 14-day
          chatting gate.
        </Text>

        {isInitialLoading ? (
          <Group justify="center" py="xl">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Loading preview…
            </Text>
          </Group>
        ) : isError || !preview ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            Could not load send preview. Please try again.
          </div>
        ) : (
          <>
            {!preview.template_enabled ? (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                <IconAlertTriangle size={18} className="mt-0.5 shrink-0" />
                <span>
                  Auto-send is off for this template. Enable it before using
                  send now.
                </span>
              </div>
            ) : null}

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              <SummaryStat
                label="Customers matched"
                value={preview.summary.total_customers}
              />
              <SummaryStat
                label="Will receive email"
                value={preview.summary.customers_will_send}
                tone="success"
              />
              <SummaryStat
                label="Skipped"
                value={preview.summary.customers_skipped}
                tone="muted"
              />
              <SummaryStat
                label="Invoices matched"
                value={preview.summary.total_invoices}
              />
              <SummaryStat
                label="Invoices to send"
                value={preview.summary.invoices_will_send}
                tone="success"
              />
              <SummaryStat
                label="Emails to send"
                value={preview.summary.emails_to_send}
                tone="success"
              />
            </div>

            <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
              <div className="flex min-h-0 flex-col rounded-lg border border-slate-200">
                <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
                  <IconUsers size={16} className="text-slate-500" />
                  <Text size="sm" fw={600}>
                    Customers & invoices
                  </Text>
                </div>
                <ScrollArea className="min-h-0 flex-1" h={360} type="auto">
                  {customers.length === 0 ? (
                    <Text size="sm" c="dimmed" p="md">
                      No customers match this template right now.
                    </Text>
                  ) : (
                    <Table striped highlightOnHover className="w-full table-fixed">
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th className="w-[24%]">Customer</Table.Th>
                          <Table.Th className="w-[16%] min-w-27">
                            Status
                          </Table.Th>
                          <Table.Th className="w-[36%]">Invoices</Table.Th>
                          <Table.Th className="w-[24%]">Recipients</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {customers.map((customer) => {
                          const isSelected =
                            customer.customer_organization_id ===
                            selectedCustomerId;
                          return (
                            <Table.Tr
                              key={customer.customer_organization_id}
                              onClick={() => {
                                setSelectedCustomerId(
                                  customer.customer_organization_id,
                                );
                              }}
                              className={
                                isSelected
                                  ? "cursor-pointer bg-indigo-50"
                                  : "cursor-pointer"
                              }
                            >
                              <Table.Td>
                                <div>
                                  <Text size="sm" fw={500}>
                                    {customer.customer_name}
                                  </Text>
                                  {customer.company_name ? (
                                    <Text size="xs" c="dimmed">
                                      {customer.company_name}
                                    </Text>
                                  ) : null}
                                </div>
                              </Table.Td>
                              <Table.Td className="align-top whitespace-nowrap">
                                {customer.status === "will_send" ? (
                                  <CustomerStatusPill status={customer.status} />
                                ) : (
                                  <div className="min-w-27 space-y-1">
                                    <CustomerStatusPill status={customer.status} />
                                    {customer.skip_reason ? (
                                      <Text
                                        size="xs"
                                        c="dimmed"
                                        className="whitespace-normal"
                                      >
                                        {customer.skip_reason}
                                      </Text>
                                    ) : null}
                                  </div>
                                )}
                              </Table.Td>
                              <Table.Td>
                                <div className="space-y-1">
                                  {customer.invoices.map((invoice) => (
                                    <div
                                      key={invoice.external_id}
                                      className="flex flex-wrap items-center gap-2 text-xs"
                                    >
                                      <Link
                                        to={`/invoice/${invoice.external_id}`}
                                        className="font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
                                        onClick={(event) =>
                                          event.stopPropagation()
                                        }
                                      >
                                        {invoice.external_id}
                                      </Link>
                                      <span className="text-slate-500">
                                        {displayCurrencyAmount(
                                          invoice.amount,
                                          invoice.currency,
                                        )}
                                      </span>
                                      <span className="text-slate-400">
                                        due {displayISODate(invoice.due_date)}
                                      </span>
                                      {invoice.days_overdue > 0 ? (
                                        <span className="text-amber-700">
                                          {invoice.days_overdue}d overdue
                                        </span>
                                      ) : null}
                                      {invoice.already_sent ? (
                                        <span className="inline-flex rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                                          already sent
                                        </span>
                                      ) : null}
                                    </div>
                                  ))}
                                </div>
                              </Table.Td>
                              <Table.Td>
                                <Text size="xs" c="dimmed">
                                  {customer.recipient_emails.length
                                    ? customer.recipient_emails.join(", ")
                                    : "—"}
                                </Text>
                              </Table.Td>
                            </Table.Tr>
                          );
                        })}
                      </Table.Tbody>
                    </Table>
                  )}
                </ScrollArea>
                {totalCustomers > 0 ? (
                  <ReceivablesPagination
                    page={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    isFetching={isPageFetching}
                    pageSize={pageSize}
                    pageSizeOptions={SEND_NOW_PREVIEW_PAGE_SIZE_OPTIONS}
                    onPageSizeChange={(nextPageSize) => {
                      setPageSize(nextPageSize);
                      setPage(1);
                    }}
                    summary={paginationSummary}
                  />
                ) : null}
              </div>

              <div className="min-h-0 rounded-lg border border-slate-200">
                <div className="flex items-center gap-2 border-b border-slate-200 px-4 py-3">
                  <IconFileInvoice size={16} className="text-slate-500" />
                  <Text size="sm" fw={600}>
                    Email draft preview
                  </Text>
                </div>
                <ScrollArea h={420} type="auto" p="md">
                  {!selectedCustomer ? (
                    <Text size="sm" c="dimmed">
                      Select a customer to preview their email draft.
                    </Text>
                  ) : selectedCustomer.status !== "will_send" ? (
                    <Text size="sm" c="dimmed">
                      {selectedCustomer.skip_reason ??
                        "No draft available for this customer."}
                    </Text>
                  ) : customerDraftQuery.isLoading ? (
                    <Group justify="center" py="xl">
                      <Loader size="sm" />
                      <Text size="sm" c="dimmed">
                        Loading email draft…
                      </Text>
                    </Group>
                  ) : customerDraftQuery.isError ? (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                      Could not load email draft. Please try again.
                    </div>
                  ) : draftBatches.length > 1 ? (
                    <div className="mb-3 flex flex-wrap gap-2">
                      {draftBatches.map((batch, index) => (
                        <button
                          key={`${batch.subject}-${index}`}
                          type="button"
                          onClick={() => setSelectedBatchIndex(index)}
                          className={`rounded-full border px-3 py-1 text-xs font-medium ${
                            selectedBatchIndex === index
                              ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                              : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                          }`}
                        >
                          Email {index + 1} ({batch.invoice_external_ids.length}{" "}
                          inv.)
                        </button>
                      ))}
                    </div>
                  ) : null}

                  {draftBatch ? (
                    <EmailDraftPreview
                      batch={draftBatch.batch}
                      customer={draftBatch.customer}
                    />
                  ) : selectedCustomer?.status === "will_send" &&
                    !customerDraftQuery.isLoading &&
                    !customerDraftQuery.isError ? (
                    <Text size="sm" c="dimmed">
                      No draft available for this customer.
                    </Text>
                  ) : null}
                </ScrollArea>
              </div>
            </div>
          </>
        )}

        <Group justify="flex-end" mt="sm">
          <Button variant="default" onClick={onClose} disabled={isSending}>
            Cancel
          </Button>
          <Button
            color="teal"
            leftSection={<IconMail size={16} />}
            onClick={onConfirmSend}
            loading={isSending}
            disabled={!canSend || isInitialLoading || isError}
          >
            Send now ({preview?.summary.emails_to_send ?? 0} emails)
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
